// AppHandoffScreen — landing page for a texted /a/<CODE> link: the two steps
// (get the app, then Add conversation + code) for moving a conversation into
// the Paiper Customer app.

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import brand from '../theme/brand';
import AppHandoffSteps from '../components/AppHandoffSteps';
import { lookupHandoffCode, HandoffLookup } from '../services/AppHandoffService';

const AppHandoffScreen: React.FC = () => {
  const { code: rawCode } = useParams<{ code: string }>();
  const code = (rawCode || '').replace(/[\s-]/g, '').toUpperCase();
  const [result, setResult] = useState<HandoffLookup | 'loading' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    setResult('loading');
    lookupHandoffCode(code)
      .then((r) => { if (!cancelled) setResult(r); })
      .catch(() => { if (!cancelled) setResult('error'); });
    return () => { cancelled = true; };
  }, [code]);

  const business = typeof result === 'object' && result.businessName ? result.businessName : '';
  let body: React.ReactNode;

  if (result === 'loading') {
    body = <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: brand.sage }} /></Box>;
  } else if (result === 'error') {
    body = <Message title="Something went wrong" detail="Check your connection and try again." />;
  } else if (result.status === 'valid') {
    body = (
      <>
        <Typography sx={{ fontFamily: brand.serif, fontSize: 28, color: brand.forest, lineHeight: 1.2, mb: 3 }}>
          {business ? `Add your ${business} conversation to the Paiper app` : 'Add your conversation to the Paiper app'}
        </Typography>
        <AppHandoffSteps code={code} url={`${window.location.origin}/a/${code}`} />
      </>
    );
  } else if (result.status === 'expired' || result.status === 'used') {
    body = (
      <Message
        title={result.status === 'expired' ? 'This code has expired' : 'This code was already used'}
        detail={`Ask ${business || 'the business'} for a new one.`}
      />
    );
  } else {
    body = <Message title="We couldn't find that code" detail="Check the link and try again." />;
  }

  return (
    <Box sx={{ minHeight: '100vh', background: brand.cream, display: 'flex', justifyContent: 'center', px: 2, py: 4, boxSizing: 'border-box' }}>
      <Box sx={{ width: '100%', maxWidth: 440 }}>{body}</Box>
    </Box>
  );
};

const Message: React.FC<{ title: string; detail: string }> = ({ title, detail }) => (
  <Box sx={{ textAlign: 'center', py: 6 }}>
    <Typography sx={{ fontFamily: brand.serif, fontSize: 26, color: brand.forest, mb: 1 }}>{title}</Typography>
    <Typography sx={{ color: brand.warmGray }}>{detail}</Typography>
  </Box>
);

export default AppHandoffScreen;
